import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api/client'

/**
 * Admin per-user TOTP override. Additive-OR with the tenant-wide
 * "manager_totp_required" default in Settings → Security — this can only
 * ADD a requirement for this specific user, never waive one the tenant/role
 * policy already imposes. Setting it false just removes this user's own
 * add-on; the org default (if any) still applies.
 */
export function useSetUserTotpRequired() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { userId: string; totpRequired: boolean }) => {
      await api.patch(`/v1/users/${params.userId}/totp-policy`, {
        totpRequired: params.totpRequired,
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}

/**
 * Admin TOTP reset — lost-device recovery. Clears the user's enrolled
 * secret (they must re-enrol from Profile → Security) and revokes every
 * live session on every platform, forcing them to sign back in. Unlike
 * password reset, there's no secret to relay out-of-band, so no prompt.
 */
export function useAdminResetTotp() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (params: { userId: string }) => {
      await api.post(`/v1/users/${params.userId}/totp-reset`)
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'users'] }),
  })
}
